import { REPORTS } from 'constants/routePaths';

import * as React from 'react';

import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import FormSection from 'components/ReportRequestV2/FormSection';
import { Link as RouterLink } from 'react-router-dom';

import { StepProps } from './types';
import { formatAgeRanges } from '../model/summary';

const pct = (f: number) => `${Math.round(f * 100)}%`;

/** Update mode: what is being updated. Identity and demographics are fixed by the original report. */
const SourceStep: React.FC<StepProps> = ({ state, errors }) => {
  const source = state.updateSource;

  if (!source) {
    return (
      <Alert
        severity="warning"
        action={
          <Button component={RouterLink} to={REPORTS} color="inherit" size="small">
            Go to reports
          </Button>
        }
      >
        {errors.updateSource ??
          'No report selected. Open a report’s menu and choose “Update report (V2)” to start an update.'}
      </Alert>
    );
  }

  const rows: [string, React.ReactNode][] = [
    ['Project', state.projectName],
    ['Client', state.clientName],
    ['Workspace', state.workspace?.name ?? '—'],
    ['Target group', source.targetGroupName],
    ['Report ID', <code key="id">{source.reportId}</code>],
    ['Ran on', new Date(source.startedAt).toLocaleString()],
    ['Countries', source.countries.join(', ').toUpperCase()],
    ['Ages', formatAgeRanges(source.ageRanges)],
    ['Gender', `${pct(source.includeFemale)} female / ${pct(source.includeMale)} male`],
    ['Rejectors', source.sansRejector ? 'Excluded' : 'Included'],
  ];

  return (
    <>
      <FormSection
        title="Report being updated"
        description="An update re-runs this report with additional products. Its identity and demographics stay the same."
      >
        <Box
          component="dl"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 3,
            rowGap: 1,
            m: 0,
            '& dt': { color: 'text.secondary' },
            '& dd': { m: 0, wordBreak: 'break-word' },
          }}
        >
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </Box>
        {(errors.projectName || errors.clientName || errors.workspace) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {[errors.projectName, errors.clientName, errors.workspace].filter(Boolean).join('. ')}
          </Alert>
        )}
      </FormSection>
      <FormSection title={`Existing products (${source.originalProducts.length})`}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          These remain on the report and cannot be removed.
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {source.originalProducts.map((p) => (
            <Chip key={p} label={p} size="small" variant="outlined" />
          ))}
        </Stack>
      </FormSection>
    </>
  );
};

export default SourceStep;
